import React from 'react';
import PropTypes from 'prop-types';

import isomorphicMatchMedia from './isomorphicMatchMedia';

/**
 * An isomorphic React wrapper for the window.matchMedia API.
 * Always assumes no match in Node-land and uses default
 * breakpoints from CCL.
 */
class MatchMedia extends React.Component {
  constructor(props) {
    super(props);
    this.mediaQueryListener = this.getMediaQueryListener();
    this.state = { matches: this.mediaQueryListener.matches };
  }

  componentDidMount() {
    this.mediaQueryListener.addListener(this.onMediaQueryMatchChange);
  }

  componentWillUnmount() {
    this.mediaQueryListener.removeListener(this.onMediaQueryMatchChange);
    this.mediaQueryListener = undefined;
  }

  shouldComponentUpdate(nextProps) {
    const mediaQueryChanged =
      nextProps.mobile !== this.props.mobile ||
      nextProps.tablet !== this.props.tablet ||
      nextProps.desktop !== this.props.desktop;

    if (mediaQueryChanged) {
      this.mediaQueryListener.removeListener(this.onMediaQueryMatchChange);
      this.mediaQueryListener = this.getMediaQueryListener();
      this.mediaQueryListener.addListener(this.onMediaQueryMatchChange);
      this.setState({ matches: this.mediaQueryListener.matches });
      // The call to `setState` has already scheduled an update, hence `return false`
      // to cancel the current render cycle.
      return false;
    }

    return true;
  }

  getMediaQuery = () => {
    const { mobile, tablet, desktop, desktopMin, tabletMin } = this.props;

    const mediaQueries = [
      mobile && `only screen and (max-width: ${tabletMin - 1}px)`,
      tablet && `only screen and (min-width: ${tabletMin}px) and (max-width: ${desktopMin - 1}px)`,
      desktop && `only screen and (min-width: ${desktopMin}px)`,
    ];

    return mediaQueries.filter((mq) => Boolean(mq)).join(', ');
  };

  getMediaQueryListener = () => {
    const mediaQuery = this.getMediaQuery();
    return isomorphicMatchMedia(mediaQuery);
  };

  onMediaQueryMatchChange = ({ matches }) => {
    this.setState({ matches });
  };

  render() {
    const { children } = this.props;
    const { matches } = this.state;

    if (typeof children === 'function') {
      /**
       * In certain scenarios, the visual difference between the media match/no-match
       * UIs might be so little that having one component per media match could be overkill.
       * In those cases, a render prop might be a better fit. For e.g.:
       *
       * <MatchMedia mobile>
       *   {(matches) => (
       *     <Button
       *       ...
       *       color={matches ? 'red' : 'green'}
       *     />
       *   )}
       * </MatchMedia>
       */
      return children(matches);
    }

    return matches ? children : null;
  }
}

MatchMedia.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  mobile: PropTypes.bool,
  tablet: PropTypes.bool,
  desktop: PropTypes.bool,
  desktopMin: PropTypes.number,
  tabletMin: PropTypes.number,
};

MatchMedia.defaultProps = {
  mobile: false,
  tablet: false,
  desktop: false,

  // Breakpoints in pixels, borrowed from CCL.
  desktopMin: 960,
  tabletMin: 540,
};

export default MatchMedia;
